import { useEffect, useState } from 'react';

import { DataList, Layout, Page } from '@nimbus-ds/patterns';
import { goTo, navigateHeader } from '@tiendanube/nexo';

import nexo from '../../nexoClient';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ICustomOrders } from '../../lib/interfaces/customOrder.interface';
import apiInstance from '../../utils/apiUtils';
import {
  Accordion,
  Alert,
  Box,
  Card,
  Icon,
  Link,
  Sidebar,
  Table,
  Tag,
  Text,
  Thumbnail,
  Title,
} from '@nimbus-ds/components';
import {
  CheckCircleIcon,
  ExternalLinkIcon,
  ForbiddenIcon,
  MailIcon,
  WhatsappIcon,
} from '@nimbus-ds/icons';
import { formatDate } from '../../utils/formatUtils';

function CustomOrderDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const API_BASE_PATH = `/custom-order`;

  const [loadingData, setLoadingData] = useState(false);
  const [order, setOrder] = useState<ICustomOrders | null>(null);
  const [openModalBriefing, setOpenModalBriefing] = useState(false);
  const [briefings, setBriefings] = useState<any[]>([]);
  const [openModalReprovedArts, setOpenModalReprovedArts] = useState(false);
  const [reprovedArts, setReprovedArts] = useState<any[]>([]);

  useEffect(() => {
    navigateHeader(nexo, {
      goTo: '/custom-orders',
      text: `${t('General.back')}`,
    });
    getData();
  }, []);

  const getData = async () => {
    setLoadingData(true);
    try {
      const result = await apiInstance.get(`${API_BASE_PATH}/${id}`);
      const data = result.data as ICustomOrders;
      setOrder(data);
    } catch (error) {
      console.log(error);
    }
    setLoadingData(false);
  };

  const prepareWhatsappLink = (number: string): string => {
    const cleanedNumber = number.replace(/[()\s-]/g, ''); // Remove parênteses, espaços e hífens
    const formattedNumber = cleanedNumber.startsWith('+')
      ? cleanedNumber
      : `+55${cleanedNumber}`;
    return `https://api.whatsapp.com/send?phone=${formattedNumber}`;
  };

  const handleOpenModalBriefing = (briefings: any) => {
    setBriefings(briefings);
    setOpenModalBriefing(true);
  };

  const handleOpenModalReprovedArts = (reprovedArts: any) => {
    setReprovedArts(reprovedArts);
    setOpenModalReprovedArts(true);
  };

  return (
    <>
      <Page>
        <Page.Header
          title={`${t('CustomOrderDetailPage.title')} ${
            order?.ns_order_number || ''
          }`}
        />
        <Page.Body>
          <Layout columns="1">
            {loadingData ? (
              <Title>Carregando</Title>
            ) : (
              <>
                {order ? (
                  <Box display="grid" gap="6">
                    <Layout columns="2 - symmetric">
                      <Card>
                        <Card.Header title="Dados do Pedido" />
                        <Card.Body>
                          <Box display="grid" gap="2">
                            <Box display="flex" gap="2">
                              <Box width="70px">
                                <Text fontWeight="bold">ID:</Text>
                              </Box>
                              <Text>#{order.ns_order_id} </Text>
                              <Link
                                as="a"
                                onClick={() =>
                                  goTo(nexo, `/orders/${order.ns_order_id}`)
                                }
                                appearance="primary"
                                title={`Visualizar pedido original`}
                              >
                                <Icon
                                  color="currentColor"
                                  source={<ExternalLinkIcon />}
                                />
                              </Link>
                            </Box>
                            <Box display="flex" gap="2">
                              <Box width="70px">
                                <Text fontWeight="bold">Número:</Text>
                              </Box>
                              <Text>{order.ns_order_number || '---'}</Text>
                            </Box>
                            <Box display="flex" gap="2">
                              <Box width="70px">
                                <Text fontWeight="bold">Situação:</Text>
                              </Box>
                              <Text>{order.custom_status_name || '---'}</Text>
                            </Box>
                            <Box display="flex" gap="2">
                              <Box width="70px">
                                <Text fontWeight="bold">Finalizado:</Text>
                              </Box>
                              {order.finished ? (
                                <Text color="success-interactive">Sim</Text>
                              ) : (
                                <Text color="danger-interactive">Não</Text>
                              )}
                            </Box>
                            <Box display="flex" gap="4">
                              <Box width="60px">
                                <Text fontWeight="bold">Produtos:</Text>
                              </Box>
                              <Text>
                                {order.custom_order_itens?.length || 1}
                              </Text>
                            </Box>
                          </Box>
                        </Card.Body>
                      </Card>
                      <Card>
                        <Card.Header title="Dados do Cliente" />
                        <Card.Body>
                          <Box display="grid" gap="2">
                            <Box display="flex" gap="2">
                              <Box width="70px">
                                <Text fontWeight="bold">ID:</Text>
                              </Box>
                              <Text>#{order.ns_customer_id} </Text>
                              <Link
                                as="a"
                                onClick={() =>
                                  goTo(
                                    nexo,
                                    `/customers/${order.ns_customer_id}`,
                                  )
                                }
                                appearance="primary"
                                title={`Visualizar dados do cliente original`}
                              >
                                <Icon
                                  color="currentColor"
                                  source={<ExternalLinkIcon />}
                                />
                              </Link>
                            </Box>
                            <Box display="flex" gap="2">
                              <Box width="70px">
                                <Text fontWeight="bold">Nome:</Text>
                              </Box>
                              <Text>{order.ns_customer_name || '---'}</Text>
                            </Box>
                            <Box display="flex" gap="2">
                              <Box width="70px">
                                <Text fontWeight="bold">E-mail:</Text>
                              </Box>
                              <Text>
                                {order.ns_customer_email ? (
                                  <Box
                                    display="flex"
                                    gap="2"
                                    alignItems="center"
                                  >
                                    {order.ns_customer_email}{' '}
                                    <Link
                                      as="a"
                                      href={`mailto:${order.ns_customer_email}`}
                                      appearance="primary"
                                      title={`Enviar e-mail para: ${order.ns_customer_email}`}
                                    >
                                      <Icon
                                        color="currentColor"
                                        source={<MailIcon />}
                                      />
                                    </Link>
                                  </Box>
                                ) : (
                                  '---'
                                )}
                              </Text>
                            </Box>
                            <Box display="flex" gap="2">
                              <Box width="70px">
                                <Text fontWeight="bold">Whatsapp:</Text>
                              </Box>
                              <Text>
                                {order.ns_customer_email ? (
                                  <Box
                                    display="flex"
                                    gap="2"
                                    alignItems="center"
                                  >
                                    {order.ns_customer_whatsapp}{' '}
                                    <Link
                                      as="a"
                                      href={prepareWhatsappLink(
                                        order.ns_customer_whatsapp,
                                      )}
                                      appearance="primary"
                                      title={`Chamar no whatsapp: ${order.ns_customer_whatsapp}`}
                                    >
                                      <Icon
                                        color="currentColor"
                                        source={<WhatsappIcon />}
                                      />
                                    </Link>
                                  </Box>
                                ) : (
                                  '---'
                                )}
                              </Text>
                            </Box>
                          </Box>
                        </Card.Body>
                      </Card>
                    </Layout>

                    <Card padding="none">
                      <Card.Header
                        padding="base"
                        title="Produtos Personalizados"
                      />
                      <Card.Body>
                        {order.custom_order_itens?.length ? (
                          <DataList>
                            {order.custom_order_itens.map((item, i) => (
                              <DataList.Row key={i} gap="1">
                                <Box display="flex" gap="4">
                                  {item.product_image ? (
                                    <Thumbnail
                                      src={item.product_image}
                                      alt={item.product_name}
                                      width="120px"
                                    />
                                  ) : (
                                    <Thumbnail
                                      alt={item.product_name}
                                      width="120px"
                                    />
                                  )}
                                  <Box
                                    flex="1"
                                    display="flex"
                                    justifyContent="space-between"
                                  >
                                    <Box display="grid" gap="2">
                                      <Tag appearance="warning">
                                        {item.custom_order_item_status || '---'}
                                      </Tag>
                                      <Text
                                        fontWeight="bold"
                                        fontSize="highlight"
                                      >
                                        {item.product_name}
                                      </Text>
                                      <Box display="flex" gap="2">
                                        <Text>SKU da Arte:</Text>
                                        <Text
                                          fontWeight="bold"
                                          color="primary-interactive"
                                        >
                                          {item.art_sku}
                                        </Text>
                                        {item.art_final_url &&
                                        item.art_final_url !== '' ? (
                                          <Link
                                            as="a"
                                            appearance="primary"
                                            href={item.art_final_url}
                                            title="Abrir arte final"
                                            target="_blank"
                                          >
                                            Visualizar arte final
                                            <Icon
                                              color="currentColor"
                                              source={<ExternalLinkIcon />}
                                            />
                                          </Link>
                                        ) : (
                                          <></>
                                        )}
                                      </Box>

                                      {item.briefing_obj?.length ? (
                                        <Link
                                          appearance="primary"
                                          onClick={() =>
                                            handleOpenModalBriefing(
                                              item.briefing_obj,
                                            )
                                          }
                                        >
                                          Historico de briefings
                                        </Link>
                                      ) : (
                                        <Text>Nenhuma briefing</Text>
                                      )}
                                      {item.art_change_detail?.length ? (
                                        <Link
                                          appearance="primary"
                                          onClick={() =>
                                            handleOpenModalReprovedArts(
                                              item.art_change_detail,
                                            )
                                          }
                                        >
                                          Historico de alterações
                                        </Link>
                                      ) : (
                                        <Text>
                                          Nenhuma solicitação de alteração
                                        </Text>
                                      )}
                                    </Box>
                                    <Text textAlign="center">
                                      <Box as="b" display="block">
                                        Ultima atualização
                                      </Box>
                                      {formatDate(`${item.updatedAt}`)}
                                    </Text>
                                  </Box>
                                </Box>
                              </DataList.Row>
                            ))}
                          </DataList>
                        ) : (
                          <></>
                        )}
                      </Card.Body>
                    </Card>
                  </Box>
                ) : (
                  <Title>Não encontrado</Title>
                )}
              </>
            )}
          </Layout>
        </Page.Body>
      </Page>
      <Sidebar
        maxWidth="60%"
        open={openModalBriefing}
        onRemove={() => setOpenModalBriefing(false)}
      >
        <Sidebar.Header padding="base" title="Historico de Briefing" />
        <Sidebar.Body padding="none">
          <Accordion>
            {briefings.map((briefing, i) => (
              <Accordion.Item index={`${i}`} key={i}>
                <Accordion.Header
                  borderTop="none"
                  title={`Briefing ${i + 1} ${
                    'reproved_msg' in briefing ? ' - Reprovado' : ''
                  }`}
                  subtitle={`Ultima atualização em ${formatDate(
                    `${
                      briefing.updatedAt
                        ? briefing.updatedAt
                        : briefing.createdAt
                    }`,
                  )}`}
                >
                  <Box
                    backgroundColor={
                      'reproved_msg' in briefing
                        ? 'danger-surface'
                        : 'primary-surface'
                    }
                    borderRadius="5"
                    marginRight="2-5"
                    padding="2-5"
                  >
                    <Icon
                      color="neutral-textHigh"
                      source={
                        'reproved_msg' in briefing ? (
                          <ForbiddenIcon size="medium" />
                        ) : (
                          <CheckCircleIcon size="medium" />
                        )
                      }
                    />
                  </Box>
                </Accordion.Header>
                <Accordion.Body>
                  <Box display="grid" gap="4">
                    {'reproved_msg' in briefing ? (
                      <Alert
                        appearance="danger"
                        title="Briefing rejeitado pelo seguinte motivo:"
                      >
                        {`"${briefing.reproved_msg}"`}
                      </Alert>
                    ) : (
                      <></>
                    )}
                    <Table>
                      <Table.Body>
                        {Object.entries(briefing).map(
                          ([key, value], rowIndex) => (
                            <>
                              {![
                                'createdAt',
                                'updatedAt',
                                'reproved_msg',
                              ].includes(key) ? (
                                <Table.Row key={rowIndex}>
                                  <Table.Cell as="th">{key}</Table.Cell>
                                  <Table.Cell as="th">
                                    {value as string}
                                  </Table.Cell>
                                </Table.Row>
                              ) : (
                                <></>
                              )}
                            </>
                          ),
                        )}
                      </Table.Body>
                    </Table>
                  </Box>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Sidebar.Body>
      </Sidebar>
      <Sidebar
        maxWidth="60%"
        open={openModalReprovedArts}
        onRemove={() => setOpenModalReprovedArts(false)}
      >
        <Sidebar.Header padding="base" title="Historico de Briefing" />
        <Sidebar.Body padding="none">
          <Accordion>
            {reprovedArts.map((art, i) => (
              <Accordion.Item index={`${i}`} key={i}>
                <Accordion.Header
                  borderTop="none"
                  title={`Solicitação de alteração da arte ${
                    'observation_papello' in art
                      ? `negada`
                      : art.status === 'review_approved'
                        ? `realiazada`
                        : `aguardando aprovação`
                  }`}
                  subtitle={`Ultima atualização em ${
                    art.updatedAt || art.createdAt
                      ? formatDate(
                          `${art.updatedAt ? art.updatedAt : art.createdAt}`,
                        )
                      : `---`
                  }`}
                >
                  <Box
                    backgroundColor={
                      'observation_papello' in art
                        ? 'danger-surface'
                        : 'primary-surface'
                    }
                    borderRadius="5"
                    marginRight="2-5"
                    padding="2-5"
                  >
                    <Icon
                      color="neutral-textHigh"
                      source={
                        'observation_papello' in art ? (
                          <ForbiddenIcon size="medium" />
                        ) : (
                          <CheckCircleIcon size="medium" />
                        )
                      }
                    />
                  </Box>
                </Accordion.Header>
                <Accordion.Body>
                  <Box display="grid" gap="4">
                    {'observation_papello' in art ? (
                      <Alert
                        appearance="danger"
                        title="Solicitação negada pelo seguinte motivo:"
                      >
                        {`"${art.observation_papello}"`}
                      </Alert>
                    ) : (
                      <></>
                    )}
                    <Table>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell as="th">Arquivo:</Table.Cell>
                          <Table.Cell as="th">
                            {art.observation_file || '---'}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell as="th">Mensagem:</Table.Cell>
                          <Table.Cell as="th">
                            {art.observation_message || '---'}
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </Box>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Sidebar.Body>
      </Sidebar>
    </>
  );
}

export default CustomOrderDetailPage;
